import { useQuery } from '@tanstack/react-query';
import { RegisterScreen } from '@/components/RegisterScreen';
import { ChildRecords } from '@/components/ChildRecords';
import { StatusPill, formatDate, todayIso } from '@/components/live';
import { listRows } from '@/api/resources';
import { usePickers } from '@/hooks/usePickers';
import { isSupabaseConfigured } from '@/lib/supabase';

interface UnitRow {
  id: string; unit_code: string | null; name: string; unit_type: 'department' | 'team'; parent_unit_id: string | null;
  parent_unit_name: string | null; manager_name: string | null; description: string | null; status: string; member_count: number;
}
interface MemberRow {
  id: string; user_name: string; user_email: string | null; reporting_manager_name: string | null; is_primary: boolean;
  effective_from: string; effective_to: string | null; status: string;
}

const UNIT_TYPES = [{ id: 'department', label: 'Department' }, { id: 'team', label: 'Team' }];
const STATUS = [{ id: 'active', label: 'Active' }, { id: 'inactive', label: 'Inactive' }];

function Members({ unit }: { unit: UnitRow }) {
  const pick = usePickers(['users']);
  return (
    <ChildRecords<MemberRow>
      addLabel="Add member" emptyText="Nobody is in this team yet."
      filters={{ org_unit_id: unit.id }} ownerId={unit.id} parent={{ org_unit_id: unit.id }}
      screenKey="org.units" table="org.user_unit_assignments" title="Members" view="user_unit_assignments"
      orderBy={{ column: 'created_at', ascending: true }}
      intro={<p className="muted">A role with "Team" record scope sees the records of everyone in the teams its user belongs to or manages, and of the people who report to them.</p>}
      defaults={{ is_primary: true, status: 'active', effective_from: todayIso() }}
      fields={[
        { key: 'user_id', label: 'Person', type: 'select', options: pick.users, required: true },
        { key: 'reporting_manager_user_id', label: 'Reports to', type: 'select', options: pick.users, placeholder: 'Nobody in particular' },
        { key: 'is_primary', label: 'Primary team', type: 'check', placeholder: 'This is their main team (one per person)' },
        { key: 'effective_from', label: 'From', type: 'date', required: true },
        { key: 'effective_to', label: 'Until', type: 'date' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS, required: true },
      ]}
      validate={(form) => ({
        effective_to: form.effective_to && String(form.effective_to) < String(form.effective_from) ? 'Cannot be before From.' : undefined,
      })}
      summary={(row) => ({
        title: row.user_name,
        badge: <StatusPill value={row.status} />,
        detail: [row.is_primary ? 'primary team' : null, row.reporting_manager_name ? `reports to ${row.reporting_manager_name}` : null,
          `from ${formatDate(row.effective_from)}`, row.effective_to ? `until ${formatDate(row.effective_to)}` : null].filter(Boolean).join(' · '),
      })}
    />
  );
}

/**
 * Teams and departments (2026-09-27). "Team" record scope on Access & scope is computed from these
 * units, their managers and their members (authz.fn_team_user_ids) - and until today nothing could
 * create a team or put anyone in one, so choosing "Team" for a role in a new organisation quietly
 * meant "Own". A team sits under a department; a department's manager sees every team under it.
 */
export default function TeamsScreen() {
  const pick = usePickers(['users']);
  const departments = useQuery({
    queryKey: ['org_units', 'departments'], enabled: isSupabaseConfigured,
    queryFn: () => listRows<{ id: string; name: string; unit_type: string }>('org_units', { filters: { unit_type: 'department' }, orderBy: { column: 'name' } }),
  });
  const departmentOptions = (departments.data ?? []).map((row) => ({ id: row.id, label: row.name }));
  return (
    <RegisterScreen<UnitRow>
      secondary={{ sum: (row) => row.member_count, unit: 'members', digits: 0 }}
      addLabel="Add team or department" exportName="teams" recordLabel={(row) => `${row.unit_type} ${row.name}`} recordNoun="unit"
      screenKey="org.units" searchColumns={['name', 'unit_code', 'parent_unit_name', 'manager_name']}
      subtitle="Departments, the teams under them, and who is in each - what 'Team' record scope is worked out from."
      table="org.org_units" title="Teams & departments" view="org_units" orderBy={{ column: 'name', ascending: true }}
      columns={[
        { key: 'name', label: 'Unit', render: (row) => <span className="cell-stack"><strong>{row.name}</strong><small className="muted">{row.unit_code ?? ''} · {row.unit_type === 'team' ? 'Team' : 'Department'}</small></span> },
        { key: 'parent_unit_name', label: 'Under', render: (row) => row.parent_unit_name ?? '--' },
        { key: 'manager_name', label: 'Manager', render: (row) => row.manager_name ?? '--' },
        { key: 'member_count', label: 'Members', align: 'right' },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      stats={[
        { key: 'dept', label: 'Departments', tone: 'info', match: (row) => row.unit_type === 'department' },
        { key: 'team', label: 'Teams', tone: 'ok', match: (row) => row.unit_type === 'team' },
        { key: 'empty', label: 'Teams with nobody', tone: 'warn', match: (row) => row.unit_type === 'team' && row.member_count === 0 },
        { key: 'nomgr', label: 'No manager', tone: 'neutral', match: (row) => !row.manager_name },
      ]}
      defaults={{ unit_type: 'team', status: 'active' }}
      fields={[
        { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Shivamogga Field Team' },
        { key: 'unit_type', label: 'Kind', type: 'select', options: UNIT_TYPES, required: true },
        { key: 'parent_unit_id', label: 'Department', type: 'select', options: departmentOptions, required: (form) => form.unit_type === 'team',
          showIf: (form) => form.unit_type === 'team', placeholder: 'Choose the department' },
        { key: 'manager_user_id', label: 'Manager', type: 'select', options: pick.users, placeholder: 'No manager',
          hint: 'A manager sees the records of the team - and of every team under a department they manage.' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS, required: true },
      ]}
    >
      {(row) => (row.unit_type === 'team' || row.member_count > 0 ? <Members unit={row} /> : <p className="muted">People join teams, not departments. Add a team under this department.</p>)}
    </RegisterScreen>
  );
}
